import React, { useState } from 'react';
import { Download, FileText, CheckCircle, ArrowLeft, AlertCircle, XCircle, ChevronRight } from 'lucide-react';
import { RetrofitSurveyData } from '../../utils/retrofitSurveyStorage';
import RetrofitExportModal from '../RetrofitExportModal';
import { exportRetrofitSurveyToPDF, FloorPlanImageData } from '../../services/retrofitPDFExportService';
import type { FormTemplate } from '../../types/formSchema';

interface RetrofitExportProps {
  onNavigateBack: () => void;
  surveyData?: RetrofitSurveyData;
  onModalStateChange?: (isOpen: boolean) => void;
  onDataUpdate?: (updater: (prev: RetrofitSurveyData) => RetrofitSurveyData) => void;
  onConvertPhotosToUrls?: () => Promise<{ success: boolean; message: string }>;
  onSaveImmediately?: () => Promise<string>;
  formTemplate?: FormTemplate; // Form builder template for custom pages
  onGetFreshFloorPlanData?: () => any; // Get latest floor plan data before export
  onSectionNavigate?: (sectionId: string) => void; // Navigate to specific section from incomplete fields
}

const RetrofitExport: React.FC<RetrofitExportProps> = ({ 
  onNavigateBack,
  surveyData,
  onModalStateChange,
  onDataUpdate,
  onConvertPhotosToUrls,
  onSaveImmediately,
  formTemplate,
  onGetFreshFloorPlanData,
  onSectionNavigate
}) => {
  // Debug: Log formTemplate immediately when component receives it
  console.log('🎯 [RetrofitExport] Received formTemplate:', {
    hasTemplate: !!formTemplate,
    templateId: formTemplate?.id,
    hasCustomPages: !!formTemplate?.customPages,
    customPagesCount: formTemplate?.customPages?.length || 0,
    hasPagesArray: !!formTemplate?.pages,
    pagesCount: formTemplate?.pages?.length || 0
  });
  
  const [showExportModal, setShowExportModal] = useState(false);
  const [additionalNotes, setAdditionalNotes] = useState(surveyData?.initial_questionnaire?.additionalNotes || '');
  
  // Notify parent when modal state changes
  React.useEffect(() => {
    onModalStateChange?.(showExportModal);
  }, [showExportModal, onModalStateChange]);

  // Sync notes from surveyData
  React.useEffect(() => {
    if (surveyData?.initial_questionnaire?.additionalNotes !== undefined) {
      setAdditionalNotes(surveyData.initial_questionnaire.additionalNotes);
    }
  }, [surveyData?.initial_questionnaire?.additionalNotes]);

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setAdditionalNotes(value);
    
    // Trigger autosave
    if (onDataUpdate) {
      onDataUpdate(prev => ({
        ...prev,
        initial_questionnaire: {
          ...prev.initial_questionnaire,
          additionalNotes: value
        }
      }));
    }
  };

  const handleExport = async (options: { 
    logoDataUrl?: string; 
    headerColor?: string;
    companyName?: string;
    companyWebsite?: string;
    companyPhone?: string;
    companyEmail?: string;
    companyAddress?: string;
    includePhotos: boolean; 
    includeFloorPlan: boolean; 
    floorPlanImages: FloorPlanImageData[];
    format?: 'pdf' | 'html' | 'both';
    onProgress?: (progress: number, stage: string) => void;
  }) => {
    if (!surveyData) {
      alert('No retrofit survey data available to export.');
      return;
    }

    try {
      // ✅ CRITICAL: Convert base64 photos to CDN URLs first (if needed)
      if (onConvertPhotosToUrls) {
        console.log('🔄 [RetrofitExport] Converting base64 photos to CDN URLs...');
        if (options.onProgress) {
          options.onProgress(2, 'Uploading photos...');
        }
        const result = await onConvertPhotosToUrls();
        if (!result.success) {
          console.warn('⚠️ [RetrofitExport] Photo conversion had issues:', result.message);
          // Continue anyway - photos might already be uploaded
        }
        console.log('✅ [RetrofitExport] Photo conversion completed');
        
        // Brief delay to ensure uploads complete
        await new Promise(resolve => setTimeout(resolve, 300));
      }
      
      // ✅ CRITICAL: Save all changes (especially wall insulation elevation data) before PDF export
      if (onSaveImmediately) {
        console.log('💾 [RetrofitExport] Saving all changes before PDF export...');
        if (options.onProgress) {
          options.onProgress(5, 'Saving latest changes...');
        }
        await onSaveImmediately();
        console.log('✅ [RetrofitExport] All changes saved successfully');
        
        // Small delay to ensure database write completes
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // ✅ CRITICAL: Get fresh floor plan data with updated calculations (if available)
      console.log('🔍 [RetrofitExport] Checking for fresh floor plan data:', {
        hasCallback: !!onGetFreshFloorPlanData,
        callbackType: typeof onGetFreshFloorPlanData,
        hasExistingFloorPlan: !!surveyData.floor_plan,
        hasLinkedData: !!surveyData.floor_plan?.linkedFloorPlanData,
        linkedDataHasRooms: !!surveyData.floor_plan?.linkedFloorPlanData?.rooms,
        linkedDataHasSiteNotes: !!surveyData.floor_plan?.linkedFloorPlanData?.siteNotesData
      });
      
      // Try to get fresh data if callback is available (only works if still on floor plan page)
      if (onGetFreshFloorPlanData) {
        console.log('🔄 [RetrofitExport] Attempting to get fresh floor plan data...');
        try {
          const freshFloorPlanData = onGetFreshFloorPlanData();
          console.log('📊 [RetrofitExport] Fresh floor plan data retrieved:', {
            hasFreshData: !!freshFloorPlanData,
            hasRooms: !!freshFloorPlanData?.rooms,
            hasSiteNotesData: !!freshFloorPlanData?.siteNotesData,
            siteNotesFloorsCount: freshFloorPlanData?.siteNotesData?.floors?.length || 0,
            siteNotesFloors: freshFloorPlanData?.siteNotesData?.floors?.map((f: any) => ({
              id: f.id,
              floorLevel: f.floorLevel,
              buildingType: f.buildingType,
              hasRooms: !!f.rooms,
              hasHighlighterBreakdown: !!f.highlighterBreakdown
            }))
          });
          
          // Use fresh data if it has rooms OR siteNotesData (for extensions)
          if (freshFloorPlanData && (freshFloorPlanData.rooms || freshFloorPlanData.siteNotesData)) {
            console.log('✅ [RetrofitExport] Got fresh floor plan data - updating surveyData', {
              hasRooms: !!freshFloorPlanData.rooms,
              hasSiteNotesData: !!freshFloorPlanData.siteNotesData,
              siteNotesFloorsCount: freshFloorPlanData.siteNotesData?.floors?.length || 0
            });
            surveyData.floor_plan = surveyData.floor_plan || {};
            surveyData.floor_plan.linkedFloorPlanData = freshFloorPlanData;
          } else {
            console.log('ℹ️ [RetrofitExport] Fresh data not available (likely navigated away from floor plan page)');
            console.log('   Using saved linkedFloorPlanData from last autosave instead');
          }
        } catch (error) {
          console.warn('⚠️ [RetrofitExport] Error getting fresh data:', error);
          console.log('   Using saved linkedFloorPlanData from last autosave instead');
        }
      } else {
        console.log('ℹ️ [RetrofitExport] No fresh data callback - using saved linkedFloorPlanData from autosave');
      }
      
      console.log('🚀 Starting Retrofit Survey PDF Export...', options);
      console.log('📋 Retrofit Survey Data:', {
        hasFloorPlan: !!surveyData.floor_plan,
        hasLinkedData: !!surveyData.floor_plan?.linkedFloorPlanData,
        floorPlanId: surveyData.floor_plan?.floorPlanId,
        hasElevations: !!surveyData.measure_specification?.wallInsulationElevations,
        elevationCount: surveyData.measure_specification?.wallInsulationElevations?.length || 0,
        hasFormTemplate: !!formTemplate,
        customPagesCount: formTemplate?.pages?.length || 0
      });
      
      // Debug: Log formTemplate details
      if (formTemplate) {
        console.log('📝 FormTemplate Details:', {
          templateId: formTemplate.id,
          totalPages: formTemplate.pages.length,
          pages: formTemplate.pages.map(p => ({
            id: p.id,
            title: p.title,
            fieldsCount: p.fields.length,
            fields: p.fields.map(f => ({ id: f.id, label: f.label, section: f.section }))
          }))
        });
        
        // Log customPages separately
        if (formTemplate.customPages) {
          console.log('📝 FormTemplate.customPages:', {
            count: formTemplate.customPages.length,
            pages: formTemplate.customPages.map(cp => ({
              id: cp.id,
              label: cp.label,
              fieldsCount: cp.fields?.length || 0,
              fields: cp.fields?.map(f => ({ id: f.id, label: f.label, type: f.type, section: f.section }))
            }))
          });
        } else {
          console.log('📝 FormTemplate.customPages: undefined or empty');
        }
      }
      
      // Add form template and custom fields data to options
      // Handle both flat and nested data formats
      let customFieldsData = surveyData.custom_fields || {};
      
      // If formTemplate exists, check if data is nested by page ID
      // Use customPages (new format) first, fall back to pages (old format)
      const pagesToCheck = formTemplate?.customPages || formTemplate?.pages || [];
      
      if (pagesToCheck.length > 0) {
        const flattenedData: Record<string, any> = {};
        
        console.log(`📦 [Export] Checking ${pagesToCheck.length} pages for nested data`);
        
        // For each custom page, check if data is nested under its page ID
        pagesToCheck.forEach(page => {
          const pageId = page.id;
          if (customFieldsData[pageId] && typeof customFieldsData[pageId] === 'object') {
            // Data is nested - extract it
            const pageTitle = (page as any).label || (page as any).title || pageId;
            console.log(`📦 [Export] Extracting nested data for page "${pageTitle}" (${pageId})`, customFieldsData[pageId]);
            Object.assign(flattenedData, customFieldsData[pageId]);
          }
        });
        
        // If we found nested data, use flattened version, otherwise use original
        if (Object.keys(flattenedData).length > 0) {
          console.log('📦 [Export] Using flattened data from nested structure', flattenedData);
          customFieldsData = flattenedData;
        } else {
          console.log('📦 [Export] No nested data found, using original data structure');
        }
      }
      
      const exportOptions = {
        ...options,
        formTemplate: formTemplate,
        customFieldsData: customFieldsData
      };
      
      console.log('📝 Custom Fields Data:', {
        hasCustomFields: !!surveyData.custom_fields,
        customFieldsKeys: Object.keys(customFieldsData),
        customFieldsCount: Object.keys(customFieldsData).length,
        originalKeys: surveyData.custom_fields ? Object.keys(surveyData.custom_fields) : []
      });
      
      await exportRetrofitSurveyToPDF(surveyData, exportOptions);
      
      console.log('✅ Retrofit Survey PDF Export completed!');
    } catch (error) {
      console.error('❌ Retrofit Survey Export failed:', error);
      throw error; // Re-throw so modal can handle it
    }
  };

  // Comprehensive incomplete fields scanner
  const scanIncompleteFields = () => {
    if (!surveyData) return [];
    
    const incomplete: Array<{ section: string; field: string; sectionId: string }> = [];
    
    // Initial Questionnaire
    if (!surveyData.initial_questionnaire?.streetAddress) incomplete.push({ section: 'Initial Questionnaire', field: 'Street Address', sectionId: 'initial_questionnaire' });
    if (!surveyData.initial_questionnaire?.postCode) incomplete.push({ section: 'Initial Questionnaire', field: 'Post Code', sectionId: 'initial_questionnaire' });
    if (!surveyData.initial_questionnaire?.surveyorName) incomplete.push({ section: 'Initial Questionnaire', field: 'Surveyor Name', sectionId: 'initial_questionnaire' });
    if (!surveyData.initial_questionnaire?.customerFullName) incomplete.push({ section: 'Initial Questionnaire', field: 'Customer Name', sectionId: 'initial_questionnaire' });
    if (!surveyData.initial_questionnaire?.dwellingType) incomplete.push({ section: 'Initial Questionnaire', field: 'Dwelling Type', sectionId: 'initial_questionnaire' });
    if (!surveyData.initial_questionnaire?.buildingAge) incomplete.push({ section: 'Initial Questionnaire', field: 'Building Age', sectionId: 'initial_questionnaire' });
    
    // Property Front
    if (!surveyData.property_front?.buildForm) incomplete.push({ section: 'Property Front', field: 'Build Form', sectionId: 'property_front' });
    if (!surveyData.property_front?.roofType) incomplete.push({ section: 'Property Front', field: 'Roof Type', sectionId: 'property_front' });
    
    // Property Rear - checking for any data
    if (!surveyData.property_rear?.wallConstruction) incomplete.push({ section: 'Property Rear', field: 'Wall Construction', sectionId: 'property_rear' });
    
    // Occupancy Assessment
    if (!surveyData.occupancy_assessment?.numberOfOccupants) incomplete.push({ section: 'Occupancy Assessment', field: 'Number of Occupants', sectionId: 'occupancy_assessment' });
    if (!surveyData.occupancy_assessment?.occupancyPattern) incomplete.push({ section: 'Occupancy Assessment', field: 'Occupancy Pattern', sectionId: 'occupancy_assessment' });
    
    // Ventilation & Condition - checking for basic completion
    const hasVentilationData = surveyData.ventilation_condition && Object.keys(surveyData.ventilation_condition).some(key => surveyData.ventilation_condition[key as keyof typeof surveyData.ventilation_condition]);
    if (!hasVentilationData) incomplete.push({ section: 'Ventilation & Condition', field: 'Section incomplete', sectionId: 'ventilation_condition' });
    
    // Heating System
    if (!surveyData.heating_system?.primaryHeatingType) incomplete.push({ section: 'Heating System', field: 'Primary Heating Type', sectionId: 'heating_system' });
    if (!surveyData.heating_system?.heatingFuel) incomplete.push({ section: 'Heating System', field: 'Heating Fuel', sectionId: 'heating_system' });
    
    // Site Assessment - checking for basic completion
    const hasSiteAssessmentData = surveyData.site_assessment && Object.keys(surveyData.site_assessment).some(key => surveyData.site_assessment[key as keyof typeof surveyData.site_assessment]);
    if (!hasSiteAssessmentData) incomplete.push({ section: 'Site Assessment', field: 'Section incomplete', sectionId: 'site_assessment' });
    
    // Main Property Measurements
    const hasWallAreas = surveyData.main_property_measurements?.wallAreas && Object.keys(surveyData.main_property_measurements.wallAreas).length > 0;
    if (!hasWallAreas) incomplete.push({ section: 'Main Property Measurements', field: 'Wall Areas', sectionId: 'main_property_measurements' });
    if (!surveyData.main_property_measurements?.roofArea) incomplete.push({ section: 'Main Property Measurements', field: 'Roof Area', sectionId: 'main_property_measurements' });
    
    // Room Details
    if (!surveyData.room_details?.rooms || surveyData.room_details.rooms.length === 0) {
      incomplete.push({ section: 'Room Details', field: 'No rooms surveyed', sectionId: 'room_details' });
    }
    
    // Initial Questionnaire - Meters (stored in initial_questionnaire)
    if (surveyData.initial_questionnaire?.hasGasMeter === null || surveyData.initial_questionnaire?.hasGasMeter === undefined) {
      incomplete.push({ section: 'Initial Questionnaire', field: 'Gas Meter Status', sectionId: 'initial_questionnaire' });
    }
    if (surveyData.initial_questionnaire?.hasElectricMeter === null || surveyData.initial_questionnaire?.hasElectricMeter === undefined) {
      incomplete.push({ section: 'Initial Questionnaire', field: 'Electric Meter Status', sectionId: 'initial_questionnaire' });
    }
    
    return incomplete;
  };

  // Calculate comprehensive survey completeness
  const calculateCompleteness = () => {
    if (!surveyData) return 0;
    
    let completed = 0;
    let total = 0;
    
    // Initial Questionnaire (8 fields - removed habitableRooms)
    total += 8;
    if (surveyData.initial_questionnaire?.streetAddress) completed++;
    if (surveyData.initial_questionnaire?.town) completed++;
    if (surveyData.initial_questionnaire?.postCode) completed++;
    if (surveyData.initial_questionnaire?.surveyorName) completed++;
    if (surveyData.initial_questionnaire?.customerFullName) completed++;
    if (surveyData.initial_questionnaire?.dwellingType) completed++;
    if (surveyData.initial_questionnaire?.buildingAge) completed++;
    
    // Property Front (2 fields)
    total += 2;
    if (surveyData.property_front?.buildForm) completed++;
    if (surveyData.property_front?.roofType) completed++;
    
    // Property Rear (1 field)
    total += 1;
    if (surveyData.property_rear?.wallConstruction) completed++;
    
    // Occupancy assessment (2 fields)
    total += 2;
    if (surveyData.occupancy_assessment?.numberOfOccupants) completed++;
    if (surveyData.occupancy_assessment?.occupancyPattern) completed++;
    
    // Ventilation & Condition (1 field)
    total += 1;
    const hasVentilationDataCompleted = surveyData.ventilation_condition && Object.keys(surveyData.ventilation_condition).some(key => surveyData.ventilation_condition[key as keyof typeof surveyData.ventilation_condition]);
    if (hasVentilationDataCompleted) completed++;
    
    // Room details (1 field)
    total += 1;
    if (surveyData.room_details?.rooms && surveyData.room_details.rooms.length > 0) completed++;
    
    // Heating system (2 fields)
    total += 2;
    if (surveyData.heating_system?.primaryHeatingType) completed++;
    if (surveyData.heating_system?.heatingFuel) completed++;
    
    // Site Assessment (1 field)
    total += 1;
    const hasSiteAssessmentDataCompleted = surveyData.site_assessment && Object.keys(surveyData.site_assessment).some(key => surveyData.site_assessment[key as keyof typeof surveyData.site_assessment]);
    if (hasSiteAssessmentDataCompleted) completed++;
    
    // Main Property Measurements (2 fields)
    total += 2;
    const hasWallAreasCompleted = surveyData.main_property_measurements?.wallAreas && Object.keys(surveyData.main_property_measurements.wallAreas).length > 0;
    if (hasWallAreasCompleted) completed++;
    if (surveyData.main_property_measurements?.roofArea) completed++;
    
    // Initial Questionnaire - Meters (2 fields)
    total += 2;
    if (surveyData.initial_questionnaire?.hasGasMeter !== null && surveyData.initial_questionnaire?.hasGasMeter !== undefined) completed++;
    if (surveyData.initial_questionnaire?.hasElectricMeter !== null && surveyData.initial_questionnaire?.hasElectricMeter !== undefined) completed++;
    
    return Math.round((completed / total) * 100);
  };

  const completeness = calculateCompleteness();
  const incompleteFields = scanIncompleteFields();

  return (
    <div className="max-w-4xl mx-auto mt-6">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <Download size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Export Survey</h2>
                <p className="text-blue-100 text-sm">Generate your comprehensive PDF report</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          
          {/* Survey Completeness Card */}
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Survey Completeness</h3>
                <p className="text-sm text-gray-600">Your survey is {completeness}% complete</p>
              </div>
              <div className="bg-white rounded-lg px-4 py-2 shadow-sm">
                <span className="text-2xl font-bold text-blue-600">{completeness}%</span>
              </div>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div 
                className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-500 rounded-full"
                style={{ width: `${completeness}%` }}
              ></div>
            </div>
            
            {completeness === 100 && (
              <div className="mt-4 flex items-center space-x-2 text-green-600">
                <CheckCircle size={18} />
                <span className="text-sm font-medium">Survey complete and ready for export!</span>
              </div>
            )}
          </div>

          {/* Survey Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-gray-900">Property Details</h4>
              </div>
              <div className="space-y-1 text-sm text-gray-700">
                <div><strong>Address:</strong> {surveyData?.initial_questionnaire?.streetAddress || 'Not set'}</div>
                <div><strong>Post Code:</strong> {surveyData?.initial_questionnaire?.postCode || 'Not set'}</div>
                <div><strong>Type:</strong> {surveyData?.initial_questionnaire?.dwellingType || 'Not specified'}</div>
              </div>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex items-center space-x-3 mb-2">
                <FileText className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Survey Data</h4>
              </div>
              <div className="space-y-1 text-sm text-gray-700">
                <div><strong>Rooms Surveyed:</strong> {surveyData?.room_details?.rooms?.length || 0}</div>
                <div><strong>Occupants:</strong> {surveyData?.occupancy_assessment?.numberOfOccupants || 'Not specified'}</div>
                <div><strong>Heating Type:</strong> {surveyData?.heating_system?.primaryHeatingType || 'Not specified'}</div>
              </div>
            </div>
          </div>

          {/* Additional Notes */}
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <label htmlFor="additionalNotes" className="block text-sm font-semibold text-gray-900 mb-2">
              Any other relevant information...
            </label>
            <textarea
              id="additionalNotes"
              value={additionalNotes}
              onChange={handleNotesChange}
              rows={6}
              placeholder="Add any other relevant information about the property or survey..."
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 resize-none"
            />
            <p className="text-xs text-gray-500 mt-2">These notes will be included in your exported PDF report.</p>
          </div>

          {/* Incomplete Fields Tracker */}
          {incompleteFields.length > 0 ? (
            <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-5">
              <div className="flex items-center space-x-2 mb-3">
                <AlertCircle size={20} className="text-orange-600" />
                <h4 className="font-semibold text-gray-900">Incomplete Fields ({incompleteFields.length})</h4>
              </div>
              <p className="text-sm text-gray-600 mb-3">The following fields are incomplete. Click on any field to navigate to that section.</p>
              <div className="max-h-64 overflow-y-auto space-y-2">
                {incompleteFields.map((item, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      // Navigate to the section
                      if (onSectionNavigate) {
                        onSectionNavigate(item.sectionId);
                      }
                    }}
                    className="w-full text-left bg-white border border-orange-200 rounded-lg p-3 hover:bg-orange-50 hover:border-orange-400 transition-all flex items-center justify-between group"
                  >
                    <div className="flex items-center space-x-3">
                      <XCircle size={16} className="text-orange-500 flex-shrink-0" />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{item.section}</div>
                        <div className="text-xs text-gray-600">{item.field}</div>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-gray-400 group-hover:text-orange-600 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5">
              <div className="flex items-center space-x-2 mb-2">
                <CheckCircle size={20} className="text-green-600" />
                <h4 className="font-semibold text-gray-900">All Required Fields Complete!</h4>
              </div>
              <p className="text-sm text-gray-600">Your survey has all required fields filled in and is ready for export.</p>
            </div>
          )}

          {/* Export Button */}
          <div className="pt-4">
            <button
              onClick={() => {
                setShowExportModal(true);
                onModalStateChange?.(true);
              }}
              disabled={!surveyData}
              className="w-full py-4 px-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-semibold text-lg rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-3 shadow-lg hover:shadow-xl"
            >
              <Download className="w-6 h-6" />
              <span>Export Retrofit Survey PDF</span>
            </button>
          </div>

          {/* Navigation */}
          <div className="pt-4 border-t border-gray-200">
            <button
              onClick={onNavigateBack}
              className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={18} />
              <span className="text-sm font-medium">Back to Extensions</span>
            </button>
          </div>
        </div>
      </div>

      {/* Export Modal */}
      {surveyData && (
        <RetrofitExportModal
          isOpen={showExportModal}
          onClose={() => {
            setShowExportModal(false);
            onModalStateChange?.(false);
          }}
          onExport={handleExport}
          surveyData={surveyData}
        />
      )}
    </div>
  );
};

export default RetrofitExport;
